import { Box, Grid2 as Grid, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Alert, Button, PlusIcon } from 'framework/ui/mui-builder';
import { useState } from 'react';
import { ListLoadingDecorator } from 'framework/list';
import { useShowContext } from 'framework/show';
import { FormFieldsData } from 'framework/form';
import CreateStaffingNoteForm from './CreateStaffingNoteForm';
import NotesList from './NotesList';
import useStaffingNotesService from './staffingNotesService';
import { StaffingDataType } from 'domain/shared/staffing';
import { DashboardItemSectionWrapper } from 'framework/ui/dashboard';

const VISIBLE_ITEMS_COUNT = 2;

interface NotesWrapperProps {
  showAllItems?: boolean;
  wrapInAccordion?: boolean;
}

const NotesWrapper = ({ showAllItems = false, wrapInAccordion = true }: NotesWrapperProps) => {
  const { data, setData } = useShowContext<StaffingDataType>();
  const projectData = data.getData();
  const { createStaffingNote, updateStaffingNote } = useStaffingNotesService();

  const [loading, setLoading] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const notes = projectData.staffingNote || [];
  const visibleItems = showAllItems ? notes : showAll ? notes : notes.slice(0, VISIBLE_ITEMS_COUNT);
  const hasMoreItems = !showAllItems && notes.length > VISIBLE_ITEMS_COUNT;

  const handleSubmitNote = async (values: FormFieldsData) => {
    setLoading(true);
    try {
      const createdNode = await createStaffingNote(String(projectData.id), {
        noteText: values.noteText,
        attachmentPaths: values.attachmentPaths || [],
      });
      setData?.({
        ...projectData,
        staffingNote: [createdNode, ...(projectData.staffingNote || [])],
      });
      setIsAddingNote(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNote = async (id: string, values: FormFieldsData) => {
    setLoading(true);
    try {
      const updatedNote = await updateStaffingNote(id, {
        noteText: values.noteText,
        attachmentPaths: values.attachmentPaths || [],
      });
      setData?.({
        ...projectData,
        staffingNote: projectData.staffingNote?.map(note => (note.id === id ? updatedNote : note)) || [],
      });
      setEditingNoteId(null);
      return updatedNote;
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (isAddingNote) {
      return (
        <Box my={2}>
          <CreateStaffingNoteForm
            onCancel={() => setIsAddingNote(false)}
            submitFormFunc={handleSubmitNote}
            afterSubmit={{
              message: 'Note has been added successfully',
            }}
          />
        </Box>
      );
    }

    if (notes.length === 0) {
      return <Alert severity="info">No records found</Alert>;
    }

    return (
      <>
        <NotesList
          items={visibleItems}
          editingNoteId={editingNoteId}
          setEditingNoteId={setEditingNoteId}
          onCancelEdit={() => setEditingNoteId(null)}
          onUpdateNote={handleUpdateNote}
        />
        {hasMoreItems && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="text" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show Less' : `Show All (${notes.length})`}
            </Button>
          </Box>
        )}
      </>
    );
  };

  const titleBlock = (
    <Grid container direction="row" justifyContent="space-between" alignItems="center" width="100%">
      <Typography variant="h4" color="primary">
        Notes
      </Typography>

      {!isAddingNote && (
        <IconButton sx={{ p: 0 }} onClick={() => setIsAddingNote(true)}>
          <PlusIcon />
        </IconButton>
      )}
    </Grid>
  );

  const content = (
    <ListLoadingDecorator loading={loading} hasBackground={true} hasPositionFixed={true}>
      {renderContent()}
    </ListLoadingDecorator>
  );

  if (!wrapInAccordion) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {titleBlock}
        </Box>

        {content}
      </Box>
    );
  }

  return (
    <DashboardItemSectionWrapper sx={{ '.MuiAccordionSummary-root': { pr: 0 } }} title={titleBlock} defaultExpanded>
      {content}
    </DashboardItemSectionWrapper>
  );
};

export default NotesWrapper;
